import React, {
  Fragment,
  forwardRef,
  useRef,
  useImperativeHandle,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Intent } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import { TranslationKey } from 'i18n/i18n.types';
import Form, { CustomFormFieldComponent, CustomValidationFunction } from 'shared/form';
import { TabProp } from 'shared/multi-tab/MultiTab';
import {
  FormTemplate,
  FormTemplateSection,
  SectionType,
} from 'data/template/template.types';
import { VpnConfig } from 'data/vpnConfig/vpnConfig.types';
import {
  AccessFunctionFormData,
  AccessFunctionInterfaceCompositeFormData,
  AccessFunctionInterfaceFormSectionType,
  AccessFunctionFieldUpdateCallback,
} from 'data/accessFunction/accessFunction.types';
import { VPNConfigurationSection, VPNConfigurationSectionRef } from 'components/VPNConfiguration';
import { populateLiInterfaceId, populateMappedId } from 'components/VPNConfiguration/utils/vpn.utils';
import { DistributorConfigurationTableData } from 'data/distributorConfiguration/distributorConfiguration.types';
import { VpnConfigTemplate } from 'data/vpnTemplate/vpnTemplate.types';
import { NodeData } from 'data/nodes/nodes.types';
import { AFMultiTabFormSection, AFMultiTabFormSectionRef } from '../AFMultiTabFormSection';
import FormSection from '../FormSection';
import FormSectionDistributorConfigs from '../FormSectionDistributorConfigs';
import { getDistributorsTableNodeName, getInterfaceFormTemplateSection } from '../../utils/accessFunctionTemplate.adapter';
import { isHeaderSelectionPanel } from './MultifacetedForm.utils';

type Props = {
  title: string,
  hintMessage?: string,
  template: FormTemplate,
  formSectionName: AccessFunctionInterfaceFormSectionType,
  formSubsectionName: string
  data: Partial<AccessFunctionFormData> | undefined,
  distributorConfigs: DistributorConfigurationTableData[],
  vpnConfigTemplates?: VpnConfigTemplate[],
  vpnConfigs?: VpnConfig[],
  isEditing: boolean,
  is5gcVcpEnabled?: boolean,
  customValidationFunctionMap?: { [key: string]: CustomValidationFunction },
  customFieldComponentMap?: { [key: string]: CustomFormFieldComponent },
  shouldValidateOnRender?: boolean
  i18nKeyPrefix: string
  localizationDisabled?: boolean
  hideTabTitle?: boolean
  hideVpnConfigs?: boolean
  hide?: 'true' | 'false',
  xcipioNodes?: NodeData[],
  hideAddButton?: boolean,
  mode?: 'create' | 'edit' | 'copy' | 'view',
  onFieldUpdate?: AccessFunctionFieldUpdateCallback,
  onDistributorUpdate?: (distributorConfiguration: DistributorConfigurationTableData) => void,
  onTabAdd?: (formSectionName: AccessFunctionInterfaceFormSectionType, tabs: TabProp[]) => void,
  onTabRemove?: (id: string | number, formSectionName: AccessFunctionInterfaceFormSectionType) => void,
  onFieldCreateNew?: AccessFunctionFieldUpdateCallback,
  onTabChange?: (tab: TabProp, formSectionName: AccessFunctionInterfaceFormSectionType) => void,
  onClickFileAction?: (param: string) => void,
}

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => AccessFunctionInterfaceCompositeFormData | null
}

export const MultifacetedFormSection = forwardRef<Ref, Props>(({
  title,
  hintMessage,
  template,
  formSectionName,
  formSubsectionName,
  data,
  distributorConfigs,
  vpnConfigTemplates,
  vpnConfigs,
  isEditing,
  is5gcVcpEnabled,
  customValidationFunctionMap,
  customFieldComponentMap,
  shouldValidateOnRender = false,
  i18nKeyPrefix,
  localizationDisabled = false,
  hideTabTitle,
  hideVpnConfigs,
  hide,
  hideAddButton = false,
  mode = 'view',
  onFieldUpdate,
  onDistributorUpdate,
  xcipioNodes,
  onTabAdd = () => null,
  onTabRemove = () => null,
  onFieldCreateNew = () => null,
  onTabChange,
  onClickFileAction = () => null,
}: Props, ref) => {
  const singleFormRef = useRef<Form>(null);
  const multiFormRef = useRef<AFMultiTabFormSectionRef>(null);
  const vpnConfigRef = useRef<VPNConfigurationSectionRef>(null);
  const headerRef = useRef<Form>(null);
  const { t } = useTranslation();

  const templateSection = getInterfaceFormTemplateSection(template, formSectionName, formSubsectionName);

  const getValues = (): AccessFunctionInterfaceCompositeFormData | null => {
    let vpnConfigFormData: VpnConfig | null = getConfiguredVPNDataExcludeCopy() ?? null;
    if (vpnConfigRef.current?.getValues() != null) {
      vpnConfigFormData = vpnConfigRef.current.getValues();
    }

    if (templateSection?.metadata?.sectionType === SectionType.distributorConfiguration) {
      return {
        afInterfaceFormData: null,
        vpnConfigData: vpnConfigFormData,
      };
    }

    if (templateSection?.metadata?.max !== undefined && templateSection.metadata.max > 1) {
      return {
        afInterfaceFormData: multiFormRef.current?.getValues() ?? null,
        vpnConfigData: vpnConfigFormData,
      };
    }

    const singleFormValues = singleFormRef.current?.getValues();
    return {
      afInterfaceFormData: singleFormValues ? [singleFormValues] : null,
      vpnConfigData: vpnConfigFormData,
    };
  };

  const isValid = (forceValidation: boolean, forceTouched: boolean): boolean => {
    if (templateSection?.metadata?.sectionType === SectionType.distributorConfiguration) {
      return (headerRef.current?.isValid(forceValidation, forceTouched) ?? true);
    }

    if (templateSection?.metadata?.max !== undefined && templateSection.metadata.max > 1) {
      return (multiFormRef.current?.isValid(forceValidation, forceTouched) ?? true);
    }

    return (singleFormRef.current?.isValid(forceValidation, forceTouched) ?? true);
  };

  useImperativeHandle(ref, () => ({
    isValid,
    getValues,
  }));

  function getConfiguredVPNData() {
    const populatedMappedId = populateMappedId(formSectionName);
    return data?.vpnConfig?.filter((vpn) => (vpn.mappedId === populatedMappedId))[0];
  }

  function getConfiguredVPNDataExcludeCopy() {
    const populatedMappedId = populateMappedId(formSectionName);
    // for Copy AF, vpn.id is undefined, filter it out.
    return data?.vpnConfig?.filter((vpn) => (vpn.id != null && vpn.mappedId === populatedMappedId))[0];
  }

  function renderVpnConfig() {
    const populatedLiInterface = populateLiInterfaceId(formSectionName);

    if (hideVpnConfigs === true) {
      return null;
    }

    return (
      <Fragment>
        <VPNConfigurationSection
          liInterface={populatedLiInterface}
          ref={vpnConfigRef}
          data={getConfiguredVPNData()}
          isEditing={isEditing}
          vpnConfigTemplates={vpnConfigTemplates}
          vpnConfigs={vpnConfigs}
        />
      </Fragment>
    );
  }

  const handleRemoveButtonClick = () => {
    if (data == null || formSectionName == null) {
      return;
    }

    const interfaceData = data[formSectionName];
    if (interfaceData == null) {
      return;
    }

    onTabRemove(interfaceData[0].id, formSectionName);
  };

  const accessFunctionInterfaces = data?.[formSectionName] ?? [];

  const metadata = templateSection?.metadata;

  const shouldShowDistributorConfiguration = metadata?.sectionType === SectionType.distributorConfiguration;
  const shouldShowAddButton = !hideAddButton && accessFunctionInterfaces.length === 0;
  const shouldShowMultiTab = metadata?.max !== undefined && metadata.max > 1;
  const shouldShowRemoveButton = metadata?.min === 0 && isEditing;
  const shouldShowHeaderSelectionPanel = isHeaderSelectionPanel(template, formSectionName);

  function renderHeaderSelectionPanel() {
    if (shouldShowHeaderSelectionPanel === false) {
      return null;
    }
    const headerSelectionTemplateSection = template[formSectionName] as FormTemplateSection;
    return (
      <div data-testid={`SelectionPanel-${formSectionName}`}>
        <FormSection
          ref={headerRef}
          name={template.formId ?? template.key}
          data={data ?? {}}
          formSectionName={formSectionName}
          templateSection={headerSelectionTemplateSection}
          onFieldUpdate={onFieldUpdate}
          i18nKeyPrefix={i18nKeyPrefix}
        />
      </div>
    );
  }

  function renderForm() {

    if (shouldShowDistributorConfiguration) {
      return (
        <Fragment>
          <h3>{title}</h3>
          {renderHeaderSelectionPanel()}
          <FormSectionDistributorConfigs
            isEditing={isEditing}
            is5gcVcpEnabled={is5gcVcpEnabled}
            data={distributorConfigs}
            formSectionName={formSectionName}
            templateSection={templateSection}
            onDistributorUpdate={onDistributorUpdate}
            instructionalText={t(metadata?.i18nSectionTypeInstructionalTextKey as TranslationKey)}
            nodeName={getDistributorsTableNodeName(
              templateSection,
              data,
              xcipioNodes,
            )}
          />
          {renderVpnConfig()}
        </Fragment>
      );
    }

    if (shouldShowAddButton) {
      const onAddSection = () => onTabAdd(formSectionName, []);

      return (
        <div data-testid={`FormSection-${formSectionName}`}>
          <h3>{title}</h3>
          {renderHeaderSelectionPanel()}
          <Button data-testid="FormSectionAddButton" icon={IconNames.PLUS} onClick={onAddSection} className="add-button" />
        </div>
      );
    }

    const generalSection = template.general as FormTemplateSection;
    const contentDeliveryVia = generalSection.fields.contentDeliveryVia?.initial;

    if (shouldShowMultiTab) {
      return (
        <div data-testid={`FormSection-${formSectionName}`}>
          <h3>{title}</h3>
          {
            contentDeliveryVia !== 'trafficManager' || formSectionName === 'dataInterfaces' ?
              renderHeaderSelectionPanel() :
              null
          }
          <AFMultiTabFormSection
            ref={multiFormRef}
            name={template.formId ?? template.key}
            data={accessFunctionInterfaces}
            templateSection={templateSection}
            onFieldUpdate={onFieldUpdate}
            isEditing={isEditing}
            formSectionName={formSectionName}
            template={template}
            onTabInterfaceAdd={onTabAdd}
            onTabInterfaceRemove={onTabRemove}
            customValidationFunctionMap={customValidationFunctionMap}
            customFieldComponentMap={customFieldComponentMap}
            shouldValidateOnRender={shouldValidateOnRender}
            onFieldCreateNew={onFieldCreateNew}
            i18nKeyPrefix={i18nKeyPrefix}
            hideTabTitle={hideTabTitle}
            onTabChange={onTabChange}
          />
          {renderVpnConfig()}
        </div>
      );
    }

    return (
      <Fragment>
        <div className="form-section-header">
          <div>
            <h3 className="form-section-title">{title}</h3>
            {hintMessage && <p className="form-section-hint-message">{hintMessage}</p>}
          </div>
          {shouldShowRemoveButton && (
            <Button data-testid="FormSectionRemove" icon={IconNames.REMOVE} minimal intent={Intent.PRIMARY} onClick={handleRemoveButtonClick}>
              {t('accessFunction.form.remove')}
            </Button>
          )}
        </div>
        {
          contentDeliveryVia !== 'trafficManager' || formSectionName === 'dataInterfaces' ?
            renderHeaderSelectionPanel() :
            null
        }
        <FormSection
          ref={singleFormRef}
          name={template.formId ?? template.key}
          hintMessage={hintMessage}
          data={data || {}}
          formSectionName={formSectionName}
          dataSection={`${formSectionName}.0`}
          templateSection={templateSection}
          onFieldUpdate={onFieldUpdate}
          customValidationFunctionMap={customValidationFunctionMap}
          customFieldComponentMap={customFieldComponentMap}
          showRemoveButton={shouldShowRemoveButton}
          onRemoveButtonClick={handleRemoveButtonClick}
          shouldValidateOnRender={shouldValidateOnRender}
          onFieldCreateNew={onFieldCreateNew}
          i18nKeyPrefix={i18nKeyPrefix}
          localizationDisabled={localizationDisabled}
          onClickFileAction={onClickFileAction}
          mode={mode}
        />
        {renderVpnConfig()}
      </Fragment>
    );
  }

  if ((!templateSection) ||
    (!isEditing && shouldShowAddButton && !shouldShowDistributorConfiguration)) {
    return null;
  }

  if (hide === 'true') {
    return null;
  }

  return (
    <Fragment>
      {renderForm()}
    </Fragment>
  );
});

export default MultifacetedFormSection;
